import api from './api';

export interface CajaUsuario {
    codigoCaja: number;
    usuario: string;
    nombreUsuario?: string;
    nombreCaja?: string;
}

export interface CajaUsuarioSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    codigoCaja?: number;
    usuario?: string;
}

export interface CajaUsuarioPagedResult {
    items: CajaUsuario[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

const pickString = (...values: unknown[]): string | undefined => {
    for (const value of values) {
        if (value == null) continue;
        const text = String(value).trim();
        if (text) return text;
    }
    return undefined;
};

const normalizeCajaUsuario = (raw: any): CajaUsuario => {
    const codigoCaja = Number(
        raw?.codigoCaja ?? raw?.CodigoCaja ?? raw?.grupoMcp ?? raw?.GrupoMcp ?? 0
    );
    const usuario = pickString(raw?.usuario, raw?.Usuario, raw?.username, raw?.Username) ?? '';
    const nombreCaja = pickString(
        raw?.nombreCaja,
        raw?.NombreCaja,
        raw?.descripcion,
        raw?.Descripcion,
        raw?.descripcionCaja,
        raw?.DescripcionCaja
    );
    const nombreUsuario = pickString(
        raw?.nombreUsuario,
        raw?.NombreUsuario,
        raw?.nombre,
        raw?.Nombre
    );

    return {
        codigoCaja,
        usuario,
        ...(nombreCaja ? { nombreCaja } : {}),
        ...(nombreUsuario ? { nombreUsuario } : {}),
    };
};

const normalizePagedResult = (data: any, fallbackPageSize: number): CajaUsuarioPagedResult => {
    const itemsRaw = data?.items ?? data?.Items ?? [];
    return {
        items: Array.isArray(itemsRaw) ? itemsRaw.map(normalizeCajaUsuario) : [],
        totalCount: Number(data?.totalCount ?? data?.TotalCount ?? 0),
        pageNumber: Number(data?.pageNumber ?? data?.PageNumber ?? 1),
        pageSize: Number(data?.pageSize ?? data?.PageSize ?? fallbackPageSize),
        totalPages: Number(data?.totalPages ?? data?.TotalPages ?? 0),
    };
};

const cajaUsuarioService = {
    getCajasUsuario: async (params: CajaUsuarioSearchParams): Promise<CajaUsuarioPagedResult> => {
        const response = await api.get('/api/CajaXusuario', { params });
        return normalizePagedResult(response.data, params.pageSize);
    },

    getCajaUsuario: async (codigoCaja: number, usuario: string): Promise<CajaUsuario> => {
        const response = await api.get(`/api/CajaXusuario/${codigoCaja}/${usuario}`);
        return normalizeCajaUsuario(response.data);
    },

    getCajasPorUsuario: async (usuario: string): Promise<CajaUsuario[]> => {
        const response = await api.get(`/api/CajaXusuario/usuario/${usuario}`);
        const data = response.data;
        const items = Array.isArray(data) ? data : (data?.items ?? data?.Items ?? []);
        return Array.isArray(items) ? items.map(normalizeCajaUsuario) : [];
    },

    getUsuariosPorCaja: async (codigoCaja: number): Promise<CajaUsuario[]> => {
        const response = await api.get(`/api/CajaXusuario/caja/${codigoCaja}`);
        const data = response.data;
        const items = Array.isArray(data) ? data : (data?.items ?? data?.Items ?? []);
        return Array.isArray(items) ? items.map(normalizeCajaUsuario) : [];
    },

    createCajaUsuario: async (cajaUsuario: CajaUsuario): Promise<CajaUsuario> => {
        const response = await api.post('/api/CajaXusuario', {
            codigoCaja: cajaUsuario.codigoCaja,
            usuario: cajaUsuario.usuario,
        });
        return normalizeCajaUsuario(response.data);
    },

    updateCajaUsuario: async (codigoCaja: number, usuario: string, cajaUsuario: CajaUsuario): Promise<void> => {
        await api.put(`/api/CajaXusuario/${codigoCaja}/${usuario}`, {
            codigoCaja: cajaUsuario.codigoCaja,
            usuario: cajaUsuario.usuario,
        });
    },

    deleteCajaUsuario: async (codigoCaja: number, usuario: string): Promise<void> => {
        await api.delete(`/api/CajaXusuario/${codigoCaja}/${usuario}`);
    }
};

export default cajaUsuarioService;
